import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiTarget, FiMessageCircle, FiTrendingUp, FiHeart, FiAward, FiClock, FiChevronDown, FiChevronUp } = FiIcons;

const Leadership = () => {
  const [expandedTopic, setExpandedTopic] = useState(null);

  const topics = [
    {
      id: 1,
      icon: FiUsers,
      title: "Team Building & Collaboration",
      description: "Learn how to build high-performing teams and foster collaboration",
      color: "from-blue-500 to-blue-600",
      content: {
        overview: "Building effective teams is one of the most critical skills for any leader. It involves understanding team dynamics, creating psychological safety, and fostering an environment where everyone can contribute their best work.",
        keyPoints: [
          "Establish clear team goals and expectations",
          "Create psychological safety for open communication",
          "Leverage diverse strengths and perspectives",
          "Build trust through consistency and transparency",
          "Facilitate effective team meetings and collaboration"
        ],
        actionItems: [
          "Conduct team-building exercises regularly",
          "Implement regular one-on-one check-ins",
          "Create team charter with shared values",
          "Establish clear communication protocols"
        ]
      }
    },
    {
      id: 2,
      icon: FiMessageCircle,
      title: "Communication & Active Listening",
      description: "Master the art of clear communication and empathetic listening",
      color: "from-green-500 to-green-600",
      content: {
        overview: "Effective communication is the foundation of leadership. It's not just about speaking clearly, but also about listening actively, understanding different perspectives, and adapting your communication style to your audience.",
        keyPoints: [
          "Practice active listening techniques",
          "Adapt communication style to different audiences",
          "Provide clear, actionable feedback",
          "Use non-verbal communication effectively",
          "Handle difficult conversations with empathy"
        ],
        actionItems: [
          "Practice the 80/20 rule (listen 80%, speak 20%)",
          "Ask open-ended questions to encourage dialogue",
          "Summarize what you hear to ensure understanding",
          "Schedule regular feedback sessions"
        ]
      }
    },
    {
      id: 3,
      icon: FiTarget,
      title: "Goal Setting & Strategic Thinking",
      description: "Develop strategic vision and set achievable goals for your team",
      color: "from-purple-500 to-purple-600",
      content: {
        overview: "Strategic thinking involves seeing the big picture while managing day-to-day operations. Leaders must set clear, achievable goals and help their teams understand how their work contributes to larger objectives.",
        keyPoints: [
          "Use SMART goals framework for clarity",
          "Align team goals with organizational strategy",
          "Break down large objectives into manageable tasks",
          "Regularly review and adjust goals as needed",
          "Celebrate milestones and achievements"
        ],
        actionItems: [
          "Create quarterly goal-setting sessions",
          "Develop visual goal tracking systems",
          "Conduct monthly progress reviews",
          "Implement OKRs (Objectives and Key Results)"
        ]
      }
    },
    {
      id: 4,
      icon: FiTrendingUp,
      title: "Performance Management",
      description: "Learn to evaluate, develop, and motivate team performance",
      color: "from-orange-500 to-orange-600",
      content: {
        overview: "Performance management is about helping team members reach their full potential. It involves setting clear expectations, providing regular feedback, and creating development opportunities.",
        keyPoints: [
          "Set clear performance expectations upfront",
          "Provide regular, constructive feedback",
          "Identify and develop individual strengths",
          "Address performance issues promptly and fairly",
          "Create individual development plans"
        ],
        actionItems: [
          "Implement weekly feedback sessions",
          "Create performance improvement plans when needed",
          "Recognize and reward good performance",
          "Provide growth opportunities and challenges"
        ]
      }
    },
    {
      id: 5,
      icon: FiHeart,
      title: "Emotional Intelligence",
      description: "Develop self-awareness and emotional management skills",
      color: "from-red-500 to-red-600",
      content: {
        overview: "Emotional intelligence is crucial for leadership success. It involves understanding your own emotions, managing them effectively, and recognizing and responding appropriately to others' emotions.",
        keyPoints: [
          "Develop self-awareness of your emotional triggers",
          "Practice emotional regulation techniques",
          "Show empathy and understanding towards others",
          "Recognize emotional cues in team dynamics",
          "Create emotionally safe environments"
        ],
        actionItems: [
          "Practice daily emotional check-ins",
          "Use mindfulness techniques for emotional regulation",
          "Seek feedback on your emotional responses",
          "Learn to pause before reacting in difficult situations"
        ]
      }
    },
    {
      id: 6,
      icon: FiAward,
      title: "Decision Making & Problem Solving",
      description: "Make confident decisions and solve complex problems effectively",
      color: "from-indigo-500 to-indigo-600",
      content: {
        overview: "Leaders are constantly faced with decisions and problems. Developing a structured approach to decision-making and problem-solving helps ensure consistent, effective outcomes.",
        keyPoints: [
          "Use structured decision-making frameworks",
          "Gather relevant information before deciding",
          "Consider multiple perspectives and alternatives",
          "Accept responsibility for decisions made",
          "Learn from both successful and unsuccessful decisions"
        ],
        actionItems: [
          "Implement decision-making templates",
          "Create problem-solving protocols",
          "Practice scenario planning exercises",
          "Conduct post-decision reviews and learning sessions"
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
            Leadership Topics
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Master the essential skills every new leader needs to succeed. Click on each topic to dive deeper into actionable strategies and best practices.
          </p>
        </motion.div>

        <div className="space-y-6">
          {topics.map((topic, index) => (
            <motion.div
              key={topic.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
            >
              <div
                className="p-8 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                onClick={() => setExpandedTopic(expandedTopic === topic.id ? null : topic.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <div className={`w-16 h-16 bg-gradient-to-r ${topic.color} rounded-2xl flex items-center justify-center`}>
                      <SafeIcon icon={topic.icon} className="text-white text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{topic.title}</h3>
                      <p className="text-gray-600 text-lg">{topic.description}</p>
                    </div>
                  </div>
                  <SafeIcon 
                    icon={expandedTopic === topic.id ? FiChevronUp : FiChevronDown} 
                    className="text-2xl text-gray-400" 
                  />
                </div>
              </div>

              {expandedTopic === topic.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="border-t border-gray-100 p-8 bg-gray-50"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-xl font-bold text-gray-900 mb-4">Overview</h4>
                      <p className="text-gray-700 mb-6">{topic.content.overview}</p>
                      
                      <h4 className="text-xl font-bold text-gray-900 mb-4">Key Points</h4>
                      <ul className="space-y-2">
                        {topic.content.keyPoints.map((point, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                            <span className="text-gray-700">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xl font-bold text-gray-900 mb-4">Action Items</h4>
                      <div className="space-y-3">
                        {topic.content.actionItems.map((item, idx) => (
                          <div key={idx} className="bg-white p-4 rounded-lg border border-gray-200">
                            <div className="flex items-center space-x-3">
                              <SafeIcon icon={FiClock} className="text-blue-500" />
                              <span className="text-gray-700">{item}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Leadership;